import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsExclamationCircle, BsX } from "react-icons/bs";
import {
  HEART_MESSAGE_MAX_CHARS,
  MAX_GENRE_SELECTIONS,
  OCCASION_OTHER_MAX_CHARS,
  ONBOARDING_GENRES,
  OTHER_OCCASION,
  OTHER_RECIPIENT,
  RECIPIENT_NAME_MAX_CHARS,
  RECIPIENT_OTHER_MAX_CHARS,
  RECIPIENT_SPECIAL_MAX_CHARS,
  RECIPIENT_SPECIAL_MIN_CHARS,
  SONG_OCCASIONS,
  SONG_RECIPIENTS,
} from "@/constants/onboardingOptions";
import { INSTRUMENTS, STYLES, TEMPOS } from "@/constants";
import type { OnboardingSessionData } from "@/utils/onboardingSession";

const MAX_STYLE_SELECTIONS = 3;

function editOrderChipId(prefix: string, label: string): string {
  return `${prefix}-${label.replace(/[^a-zA-Z0-9]/g, "")}`;
}

interface EditOrderDetailsModalProps {
  isOpen: boolean;
  isDark: boolean;
  data: OnboardingSessionData;
  onClose: () => void;
  onSave: (data: OnboardingSessionData) => void;
}

function FieldError({
  isDark,
  variant,
  message,
}: {
  isDark: boolean;
  variant: "error" | "info";
  message: string;
}) {
  const isError = variant === "error";
  return (
    <div
      className={` hidden items-center gap-2 rounded-[10px] border px-3 py-2 ${
        isError
          ? isDark
            ? "border-[#ff4d4f] bg-[#ff4d4f]/10 text-[#fdfdfd]"
            : "border-[#ff4d4f] bg-[#fff1f0] text-[#16283d]"
          : isDark
            ? "border-[#0e76ff] bg-[#0e76ff]/10 text-[#fdfdfd]"
            : "border-[#0e76ff] bg-[#e7f1ff] text-[#16283d]"
      }`}
    >
      <BsExclamationCircle
        className={`shrink-0 ${isError ? "text-[#ff4d4f]" : "text-[#0e76ff]"}`}
        size={16}
      />
      <span className="text-[13px] font-normal">{message}</span>
    </div>
  );
}

export default function EditOrderDetailsModal({
  isOpen,
  isDark,
  data,
  onClose,
  onSave,
}: EditOrderDetailsModalProps) {
  const { t } = useTranslation();
  const [recipientName, setRecipientName] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState<string | null>(null);
  const [otherRecipientText, setOtherRecipientText] = useState("");
  const [selectedOccasion, setSelectedOccasion] = useState<string | null>(null);
  const [otherOccasionText, setOtherOccasionText] = useState("");
  const [selectedGenres, setSelectedGenres] = useState<Set<string>>(new Set());
  const [selectedStyles, setSelectedStyles] = useState<Set<string>>(new Set());
  const [selectedTempo, setSelectedTempo] = useState<string | null>(null);
  const [selectedInstruments, setSelectedInstruments] = useState<Set<string>>(new Set());
  const [qualities, setQualities] = useState("");
  const [memories, setMemories] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setRecipientName(data.recipientName);
    setSelectedRecipient(data.selectedRecipient);
    setOtherRecipientText(data.otherRecipientText);
    setSelectedOccasion(data.selectedOccasion);
    setOtherOccasionText(data.otherOccasionText);
    setSelectedGenres(new Set(data.selectedGenres));
    setSelectedStyles(new Set(data.selectedStyles));
    setSelectedTempo(data.selectedTempo);
    setSelectedInstruments(new Set(data.selectedInstruments));
    setQualities(data.recipientSpecialDescription);
    setMemories(data.favoriteMemoriesDescription);
    setMessage(data.heartMessageDescription);
  }, [isOpen, data]);

  if (!isOpen) return null;

  const isRecipientNameOverLimit = recipientName.length > RECIPIENT_NAME_MAX_CHARS;
  const isRecipientNameValid = recipientName.trim().length > 0 && !isRecipientNameOverLimit;

  const isOtherRecipientOverLimit = otherRecipientText.length > RECIPIENT_OTHER_MAX_CHARS;

  const isRecipientValid =
    selectedRecipient !== null &&
    (selectedRecipient !== OTHER_RECIPIENT ||
      (otherRecipientText.trim().length > 0 && !isOtherRecipientOverLimit));

  const isOtherOccasionOverLimit = otherOccasionText.length > OCCASION_OTHER_MAX_CHARS;

  const isOccasionValid =
    selectedOccasion !== null &&
    (selectedOccasion !== OTHER_OCCASION ||
      (otherOccasionText.trim().length > 0 && !isOtherOccasionOverLimit));

  const isGenreValid = selectedGenres.size > 0 && selectedStyles.size > 0;

  const isTempoInstrumentValid = selectedTempo !== null && selectedInstruments.size > 0;

  const isQualitiesOverLimit = qualities.length > RECIPIENT_SPECIAL_MAX_CHARS;
  const isQualitiesUnderMin =
    qualities.trim().length > 0 && qualities.trim().length < RECIPIENT_SPECIAL_MIN_CHARS;
  const isQualitiesValid =
    qualities.trim().length >= RECIPIENT_SPECIAL_MIN_CHARS && !isQualitiesOverLimit;

  const isMemoriesOverLimit = memories.length > RECIPIENT_SPECIAL_MAX_CHARS;
  const isMemoriesUnderMin =
    memories.trim().length > 0 && memories.trim().length < RECIPIENT_SPECIAL_MIN_CHARS;
  const isMemoriesValid =
    memories.trim().length >= RECIPIENT_SPECIAL_MIN_CHARS && !isMemoriesOverLimit;

  const isMessageOverLimit = message.length > HEART_MESSAGE_MAX_CHARS;
  const isMessageValid = !isMessageOverLimit;

  const isSaveEnabled =
    isRecipientNameValid &&
    isRecipientValid &&
    isOccasionValid &&
    isGenreValid &&
    isTempoInstrumentValid &&
    isQualitiesValid &&
    isMemoriesValid &&
    isMessageValid;

  const selectRecipient = (recipient: string) => {
    setSelectedRecipient((prev) => {
      if (prev === recipient) {
        if (recipient === OTHER_RECIPIENT) {
          setOtherRecipientText("");
        }
        return null;
      }
      if (recipient !== OTHER_RECIPIENT) {
        setOtherRecipientText("");
      }
      return recipient;
    });
  };

  const selectOccasion = (occasion: string) => {
    setSelectedOccasion((prev) => {
      if (prev === occasion) {
        if (occasion === OTHER_OCCASION) {
          setOtherOccasionText("");
        }
        return null;
      }
      if (occasion !== OTHER_OCCASION) {
        setOtherOccasionText("");
      }
      return occasion;
    });
  };

  const toggleGenre = (genre: string) => {
    setSelectedGenres((prev) => {
      const next = new Set(prev);
      if (next.has(genre)) {
        next.delete(genre);
      } else if (next.size < MAX_GENRE_SELECTIONS) {
        next.add(genre);
      }
      return next;
    });
  };

  const toggleStyle = (style: string) => {
    setSelectedStyles((prev) => {
      const next = new Set(prev);
      if (next.has(style)) {
        next.delete(style);
      } else if (next.size < MAX_STYLE_SELECTIONS) {
        next.add(style);
      }
      return next;
    });
  };

  const toggleTempo = (tempo: string) => {
    setSelectedTempo((prev) => (prev === tempo ? null : tempo));
  };

  const toggleInstrument = (instrument: string) => {
    setSelectedInstruments((prev) => {
      const next = new Set(prev);
      if (next.has(instrument)) {
        next.delete(instrument);
      } else {
        next.add(instrument);
      }
      return next;
    });
  };

  const chipClass = (isSelected: boolean) =>
    [
      "cursor-pointer rounded-full px-4 py-2 text-[12px] sm:text-[16px] leading-normal transition-colors whitespace-nowrap",
      isSelected
        ? isDark
          ? "border-[1.5px] border-[#0e76ff] bg-[#264a73] font-bold text-[#fdfdfd]"
          : "border-[1.5px] border-[#0e76ff] bg-[#e7f1ff] font-bold text-[#0e76ff]"
        : isDark
          ? "border-2 border-[#004399] bg-transparent font-normal text-[#fdfdfd]"
          : "border-2 border-[#d2e5ff] bg-white font-normal text-[#16283d]",
    ].join(" ");

  const textareaBorderClass = (isOverLimit: boolean) =>
    isOverLimit
      ? "border-[#ff4d4f]"
      : isDark
        ? "border-[#264a73]"
        : "border-[#d2e5ff]";

  const textareaClass = (isOverLimit: boolean) =>
    `w-full resize-none rounded-[10px] border px-4 py-3 pb-8 text-[14px] outline-none transition-colors ${
      isDark
        ? `${textareaBorderClass(isOverLimit)} bg-[#1e3554] text-[#fdfdfd] placeholder:text-[#94a3b8]`
        : `${textareaBorderClass(isOverLimit)} bg-white text-[#16283d] placeholder:text-[#a1a7b0]`
    }`;

  const handleSave = () => {
    if (!isSaveEnabled) return;

    onSave({
      ...data,
      recipientName,
      selectedRecipient,
      otherRecipientText,
      selectedOccasion,
      otherOccasionText,
      selectedGenres: [...selectedGenres],
      selectedStyles: [...selectedStyles],
      selectedTempo,
      selectedInstruments: [...selectedInstruments],
      recipientSpecialDescription: qualities,
      favoriteMemoriesDescription: memories,
      heartMessageDescription: message,
    });
    onClose();
  };

  return (
    <div
      id="editOrderDetailsOverlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-2 py-0"
      onClick={onClose}
      role="presentation"
    >
      <div
        id="editOrderDetailsDialog"
        className={`relative flex h-[98%]  w-full max-w-[560px] flex-col overflow-hidden rounded-[10px] border shadow-lg ${
          isDark ? "border-[#264a73] bg-[#1e3554]" : "border-[#0E76FF] bg-white"
        }`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-order-details-title"
      >
        <div
          id="editOrderDetailsHeader"
          className={`flex shrink-0 items-center justify-between border-b px-5 py-4 ${
            isDark ? "border-[#264a73]" : "border-[#eaebed]"
          }`}
        >
          <h2
            id="edit-order-details-title"
            className={`text-[18px] font-bold ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
          >
            {t("onboarding.review.editOrderDetails")}
          </h2>
          <button
            id="editOrderDetailsClose"
            type="button"
            onClick={onClose}
            aria-label={t("onboarding.review.closeEdit")}
            className={`flex size-8 items-center justify-center rounded-full ${
              isDark ? "text-[#94a3b8] hover:text-[#fdfdfd]" : "text-[#687585] hover:text-[#16283d]"
            }`}
          >
            <BsX size={24} />
          </button>
        </div>

        <div className={`flex flex-1 flex-col gap-2 sm:gap-5 px-5 py-5 overflow-y-scroll`}>
          <div
            id="editOrderDetailsForm"
            className={`custom-scrollbar flex flex-1 flex-col gap-5 max-h-[100vh - 150px] overflow-y-auto sm:px-5 py-3 px-3 sm:py-5 border-1 sm:border-2 rounded-[10px] ${isDark ? "border-[#264a73] dark" : "border-[#EAEBED]" }`}
          >
            <div className="flex flex-col gap-2">
              <span
                id="editOrderLabelName"
                className={`text-[14px] sm:text-[18px]  font-semibold mb-[10px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
              >
                {t("onboarding.review.name")}
              </span>
              {isRecipientNameOverLimit && (
                <FieldError
                  isDark={isDark}
                  variant="error"
                  message={t("onboarding.songRecipientName.characterLimitError")}
                />
              )}
              <input
                id="editOrderRecipientName"
                type="text"
                value={recipientName}
                onChange={(e) =>
                  setRecipientName(e.target.value.slice(0, RECIPIENT_NAME_MAX_CHARS))
                }
                placeholder={t("onboarding.songRecipientName.placeholder")}
                className={`w-full rounded-[10px] border px-4 py-3 text-[14px] outline-none ${
                  isDark
                    ? "border-[#264a73] bg-[#1e3554] text-[#fdfdfd] placeholder:text-[#94a3b8]"
                    : "border-[#d2e5ff] bg-white text-[#16283d] placeholder:text-[#a1a7b0]"
                }`}
              />
            </div>

            <div className="flex flex-col gap-2">
              <span
                id="editOrderLabelSong"
                className={`text-[14px] sm:text-[18px]  font-semibold mb-[10px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
              >
                {t("onboarding.review.song")}
              </span>
              {selectedRecipient === OTHER_RECIPIENT && (
                <>
                  {isOtherRecipientOverLimit && (
                    <FieldError
                      isDark={isDark}
                      variant="error"
                      message={t("onboarding.songRecipient.characterLimitError")}
                    />
                  )}
                  <textarea
                    id="editOrderOtherRecipientText"
                    value={otherRecipientText}
                    onChange={(e) => setOtherRecipientText(e.target.value)}
                    placeholder={t("onboarding.songRecipient.placeholder")}
                    rows={3}
                    className={textareaClass(isOtherRecipientOverLimit).replace(" pb-8", "")}
                  />
                  <span
                    id="editOrderOtherRecipientCharCount"
                    className={`self-end text-[12px] ${
                      isOtherRecipientOverLimit
                        ? "text-[#ff4d4f]"
                        : isDark
                          ? "text-[#94a3b8]"
                          : "text-[#a1a7b0]"
                    }`}
                  >
                    {otherRecipientText.length}/{RECIPIENT_OTHER_MAX_CHARS}
                  </span>
                </>
              )}
              <div id="editOrderRecipientChips" className="flex flex-wrap gap-[5px] sm:gap-2">
                {SONG_RECIPIENTS.map((recipient) => (
                  <button
                    key={recipient}
                    id={editOrderChipId("editOrderRecipient", recipient)}
                    type="button"
                    onClick={() => selectRecipient(recipient)}
                    className={chipClass(selectedRecipient === recipient)}
                  >
                    {recipient}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span
                id="editOrderLabelOccasion"
                className={`text-[14px] sm:text-[18px]  font-semibold mb-[10px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
              >
                {t("onboarding.review.occasion")}
              </span>
              {selectedOccasion === OTHER_OCCASION && (
                <>
                  {isOtherOccasionOverLimit && (
                    <FieldError
                      isDark={isDark}
                      variant="error"
                      message={t("onboarding.songOccasion.characterLimitError")}
                    />
                  )}
                  <textarea
                    id="editOrderOtherOccasionText"
                    value={otherOccasionText}
                    onChange={(e) => setOtherOccasionText(e.target.value)}
                    placeholder={t("onboarding.songOccasion.placeholder")}
                    rows={3}
                    className={textareaClass(isOtherOccasionOverLimit).replace(" pb-8", "")}
                  />
                  <span
                    id="editOrderOtherOccasionCharCount"
                    className={`self-end text-[12px] ${
                      isOtherOccasionOverLimit
                        ? "text-[#ff4d4f]"
                        : isDark
                          ? "text-[#94a3b8]"
                          : "text-[#a1a7b0]"
                    }`}
                  >
                    {otherOccasionText.length}/{OCCASION_OTHER_MAX_CHARS}
                  </span>
                </>
              )}
              <div
                className={`rounded-[10px] border-0 `}
              >
                <div id="editOrderOccasionChips" className="flex flex-wrap gap-[5px] sm:gap-2">
                  {SONG_OCCASIONS.map((occasion) => (
                    <button
                      key={occasion}
                      id={editOrderChipId("editOrderOccasion", occasion)}
                      type="button"
                      onClick={() => selectOccasion(occasion)}
                      className={chipClass(selectedOccasion === occasion)}
                    >
                      {occasion}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span
                id="editOrderLabelGenre"
                className={`text-[14px] sm:text-[18px]  font-semibold mb-[10px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
              >
                {t("onboarding.review.genre")}
              </span>
              <div id="editOrderGenreChips" className="flex flex-wrap gap-2">
                {ONBOARDING_GENRES.map((genre) => (
                  <button
                    key={genre}
                    id={editOrderChipId("editOrderGenre", genre)}
                    type="button"
                    onClick={() => toggleGenre(genre)}
                    className={chipClass(selectedGenres.has(genre))}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span
                id="editOrderLabelStyles"
                className={`text-[14px] sm:text-[18px]  font-semibold mb-[10px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
              >
                {t("onboarding.step1.stylesLabel")}
              </span>
              <div id="editOrderStyleChips" className="flex flex-wrap gap-[5px] sm:gap-2">
                {STYLES.map((style) => (
                  <button
                    key={style}
                    id={editOrderChipId("editOrderStyle", style)}
                    type="button"
                    onClick={() => toggleStyle(style)}
                    className={chipClass(selectedStyles.has(style))}
                  >
                    {style}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span
                id="editOrderLabelTempo"
                className={`text-[14px] sm:text-[18px]  font-semibold mb-[10px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
              >
                {t("onboarding.step2.tempoLabel")}
              </span>
              <div id="editOrderTempoChips" className="flex flex-wrap gap-[5px] sm:gap-2">
                {TEMPOS.map((tempo) => (
                  <button
                    key={tempo}
                    id={editOrderChipId("editOrderTempo", tempo)}
                    type="button"
                    onClick={() => toggleTempo(tempo)}
                    className={chipClass(selectedTempo === tempo)}
                  >
                    {tempo}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span
                id="editOrderLabelInstruments"
                className={`text-[14px] sm:text-[18px]  font-semibold mb-[10px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
              >
                {t("onboarding.step2.instrumentLabel")}
              </span>
              <div id="editOrderInstrumentChips" className="flex flex-wrap gap-[5px] sm:gap-2">
                {INSTRUMENTS.map((instrument) => (
                  <button
                    key={instrument}
                    id={editOrderChipId("editOrderInstrument", instrument)}
                    type="button"
                    onClick={() => toggleInstrument(instrument)}
                    className={chipClass(selectedInstruments.has(instrument))}
                  >
                    {instrument}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span
                id="editOrderLabelQualities"
                className={`text-[14px] sm:text-[18px]  font-semibold mb-[10px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
              >
                {t("onboarding.review.qualities")}
              </span>
              {isQualitiesOverLimit && (
                <FieldError
                  isDark={isDark}
                  variant="error"
                  message={t("onboarding.recipientSpecial.characterLimitError")}
                />
              )}
              {isQualitiesUnderMin && !isQualitiesOverLimit && (
                <FieldError
                  isDark={isDark}
                  variant="info"
                  message={t("onboarding.recipientSpecial.minCharactersError", {
                    count: qualities.trim().length,
                  })}
                />
              )}
              <textarea
                id="editOrderQualities"
                value={qualities}
                onChange={(e) => setQualities(e.target.value)}
                placeholder={t("onboarding.recipientSpecial.placeholder")}
                rows={3}
                className={textareaClass(isQualitiesOverLimit).replace(" pb-8", "")}
              />
              <span
                id="editOrderQualitiesCharCount"
                className={`self-end text-[12px] ${
                  isQualitiesOverLimit
                    ? "text-[#ff4d4f]"
                    : isDark
                      ? "text-[#94a3b8]"
                      : "text-[#a1a7b0]"
                }`}
              >
                {qualities.length}/{RECIPIENT_SPECIAL_MAX_CHARS}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <span
                id="editOrderLabelMemories"
                className={`text-[14px] sm:text-[18px]  font-semibold mb-[10px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
              >
                {t("onboarding.review.specialMemories")}
              </span>
              {isMemoriesOverLimit && (
                <FieldError
                  isDark={isDark}
                  variant="error"
                  message={t("onboarding.favoriteMemories.characterLimitError")}
                />
              )}
              {isMemoriesUnderMin && !isMemoriesOverLimit && (
                <FieldError
                  isDark={isDark}
                  variant="info"
                  message={t("onboarding.favoriteMemories.minCharactersError", {
                    count: memories.trim().length,
                  })}
                />
              )}
              <textarea
                id="editOrderMemories"
                value={memories}
                onChange={(e) => setMemories(e.target.value)}
                placeholder={t("onboarding.favoriteMemories.placeholder")}
                rows={3}
                className={textareaClass(isMemoriesOverLimit).replace(" pb-8", "")}
              />
              <span
                id="editOrderMemoriesCharCount"
                className={`self-end text-[12px] ${
                  isMemoriesOverLimit
                    ? "text-[#ff4d4f]"
                    : isDark
                      ? "text-[#94a3b8]"
                      : "text-[#a1a7b0]"
                }`}
              >
                {memories.length}/{RECIPIENT_SPECIAL_MAX_CHARS}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <span
                id="editOrderLabelMessage"
                className={`text-[14px] sm:text-[18px]  font-semibold mb-[10px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
              >
                {t("onboarding.review.specialMessage")}
              </span>
              {isMessageOverLimit && (
                <FieldError
                  isDark={isDark}
                  variant="error"
                  message={t("onboarding.heartMessage.characterLimitError")}
                />
              )}
              <textarea
                id="editOrderHeartMessage"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("onboarding.heartMessage.placeholder")}
                rows={3}
                className={textareaClass(isMessageOverLimit).replace(" pb-8", "")}
              />
              <span
                id="editOrderHeartMessageCharCount"
                className={`self-end text-[12px] ${
                  isMessageOverLimit
                    ? "text-[#ff4d4f]"
                    : isDark
                      ? "text-[#94a3b8]"
                      : "text-[#a1a7b0]"
                }`}
              >
                {message.length}/{HEART_MESSAGE_MAX_CHARS}
              </span>
            </div>
           </div>
        

          <div
            id="editOrderDetailsFooter"
            className={`shrink-0 border-t px-0 sm:px-5 py-4 ${isDark ? "border-[#264a73] !border-0" : "border-[#eaebed] !border-0"}`}
          >
            <button
              type="button"
              id="editOrderSaveChanges"
              onClick={handleSave}
              disabled={!isSaveEnabled}
              className={`h-[52px] w-full rounded-full text-[16px] font-bold tracking-[0.32px] uppercase transition-colors ${
                isSaveEnabled ? "cursor-pointer text-white" : "cursor-not-allowed text-[#a1a7b0]"
              }`}
              style={
                isSaveEnabled
                  ? { background: "linear-gradient(90deg, #f95a38 0%, #fc792e 50%, #ff9823 100%)" }
                  : { background: isDark ? "#264a73" : "#eaebed" }
              }
            >
              {t("onboarding.review.saveChanges")}
            </button>
          </div>
          
        </div>
      </div>
    </div>
  );
}
